import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom'
// material
import { alpha, experimentalStyled as styled } from '@material-ui/core/styles'
import { Box, Link, Button, Drawer, Typography, Stack, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip, Switch } from '@material-ui/core'
import { ChevronLeft, ChevronRight, Logout, Settings, MenuBook, Layers, Book, Computer, Timeline, ArrowBack, History, AssignmentInd } from '@material-ui/icons'
// hooks
import useAuth from '../../hooks/useAuth'
import useIsMountedRef from '../../hooks/useIsMountedRef'
import useSettings from '../../hooks/useSettings'
// routes
import { PATH_DASHBOARD, PATH_DOCS } from '../../routes/paths'
// components
import DarkModeToggle from '../../components/DarkModeToggle'
import Logo from '../../components/Logo'
import HawkOwlLogo from '../../components/HawkOwlLogo'
import MyAvatar from '../../components/MyAvatar'
import Scrollbar from '../../components/Scrollbar'
import NavSection from '../../components/NavSection'
import { MHidden } from '../../components/@material-extend'
//
import sidebarConfigRaw from './SidebarConfig'
import { DocIcon } from '../../assets'
import { VERSION } from 'src/constants/DefaultValues'
import { useSnackbar } from 'notistack'

// ----------------------------------------------------------------------

const DRAWER_WIDTH = 280
const COLLAPSED_WIDTH = 64

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    height: '100vh',
    position: 'relative',
  },
}))

const AccountStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2, 2.5),
  borderRadius: theme.shape.borderRadiusSm,
  backgroundColor: theme.palette.grey[500_12],
}))

const DocStyle = styled('div')(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.shape.borderRadiusMd,
  backgroundColor:
    theme.palette.mode === 'light'
      ? alpha(theme.palette.primary.main, 0.08)
      : theme.palette.primary.lighter,
}))

// ----------------------------------------------------------------------

DashboardSidebar.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
  isCollapsed: PropTypes.bool,
  onToggleCollapse: PropTypes.func,
}

export default function DashboardSidebar({ isOpenSidebar, onCloseSidebar, isCollapsed, onToggleCollapse }) {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const isMountedRef = useIsMountedRef()
  const { method, logout } = useAuth();
  const { themeMode, onChangeMode } = useSettings();
  const user = JSON.parse(localStorage.getItem('user'))
  const userType = user?.user_type
  const [openLogoutDialog, setOpenLogoutDialog] = useState(false)

  // Filter config based on hideForRoles and showForRoles
  let sidebarConfig = sidebarConfigRaw?.map((section) => ({
    ...section,
    items: section.items.filter((item) => {
      if (item.showForRoles) return item.showForRoles.includes(userType);
      if (item.hideForRoles) return !item.hideForRoles.includes(userType);
      return true;
    }),
  }))

  const isSuperHawkOwl = userType === 1 || user?.user_id === 1 || user?.user_id === 28 || user?.user_id === 39;

  if (pathname.startsWith('/dashboard/hawkowl')) {
    let hawkOwlItems = [
      {
        title: 'Employee Insights',
        path: '/dashboard/hawkowl',
        icon: <MenuBook />
      },
      {
        title: 'My Timesheet',
        path: '/dashboard/hawkowl?tab=my-timesheet',
        icon: <Layers />,
        children: [
          { title: 'My timesheet', path: '/dashboard/hawkowl?tab=my-timesheet&sub=my-timesheet' },
          { title: 'My keystrokes', path: '/dashboard/hawkowl?tab=my-timesheet&sub=keystrokes' },
          { title: 'My applications', path: '/dashboard/hawkowl?tab=my-timesheet&sub=applications' },
          { title: 'My websites', path: '/dashboard/hawkowl?tab=my-timesheet&sub=websites' },
          { title: 'My screenshots', path: '/dashboard/hawkowl?tab=my-timesheet&sub=screenshots' },
          { title: 'My application usage details', path: '/dashboard/hawkowl?tab=my-timesheet&sub=application-usage' },
          { title: 'My website usage details', path: '/dashboard/hawkowl?tab=my-timesheet&sub=website-usage' },
          { title: 'My profile settings', path: '/dashboard/hawkowl?tab=my-timesheet&sub=profile-settings' }
        ]
      },
      {
        title: 'Timesheets',
        path: '/dashboard/hawkowl?tab=timesheets&sub=team-timesheet',
        icon: <Book />,
        children: [
          { title: 'Team timesheet', path: '/dashboard/hawkowl?tab=timesheets&sub=team-timesheet' },
          { title: 'Project hours', path: '/dashboard/hawkowl?tab=timesheets&sub=project-hours' },
          { title: 'Attendance', path: '/dashboard/hawkowl?tab=timesheets&sub=attendance' },
          { title: 'Who is working today', path: '/dashboard/hawkowl?tab=timesheets&sub=who-is-working' },
          { title: 'Task tracker', path: '/dashboard/hawkowl?tab=timesheets&sub=task-tracker' },
          { title: 'Project work details', path: '/dashboard/hawkowl?tab=timesheets&sub=project-work-details' }
        ]
      },
      {
        title: 'Monitoring',
        path: '/dashboard/hawkowl?tab=monitoring&sub=keystrokes',
        icon: <Computer />,
        children: [
          { title: 'Keystrokes used', path: '/dashboard/hawkowl?tab=monitoring&sub=keystrokes' },
          { title: 'Applications used', path: '/dashboard/hawkowl?tab=monitoring&sub=applications' },
          { title: 'Websites used', path: '/dashboard/hawkowl?tab=monitoring&sub=websites' },
          { title: 'Screenshots', path: '/dashboard/hawkowl?tab=monitoring&sub=screenshots' },
          { title: 'Application usage details', path: '/dashboard/hawkowl?tab=monitoring&sub=application-usage' },
          { title: 'Website usage details', path: '/dashboard/hawkowl?tab=monitoring&sub=website-usage' }
        ]
      },
      {
        title: 'Productivity',
        path: '/dashboard/hawkowl?tab=productivity&sub=productivity-report',
        icon: <Timeline />,
        children: [
          { title: 'Productivity report', path: '/dashboard/hawkowl?tab=productivity&sub=productivity-report' },
          { title: 'Productive hours', path: '/dashboard/hawkowl?tab=productivity&sub=productive-hours' },
          { title: 'Projects worked on', path: '/dashboard/hawkowl?tab=productivity&sub=projects-worked-on' },
          { title: 'Productive employees', path: '/dashboard/hawkowl?tab=productivity&sub=productive-employees' },
          { title: 'Categories worked', path: '/dashboard/hawkowl?tab=productivity&sub=categories-worked' },
          { title: 'Productive websites', path: '/dashboard/hawkowl?tab=productivity&sub=productive-websites' },
          { title: 'Productive applications', path: '/dashboard/hawkowl?tab=productivity&sub=productive-applications' }
        ]
      },
      {
        title: 'HawkOwl settings',
        path: '/dashboard/hawkowl?tab=settings',
        icon: <Settings />
      },
      {
        title: 'HawkOwl Logs',
        path: '/dashboard/hawkowl?tab=system-logs',
        icon: <History />
      },
      {
        title: 'Apps',
        path: '/dashboard/hawkowl?tab=apps',
        icon: <Computer />
      }
    ];

    if (userType === 2 && !isSuperHawkOwl) {
      hawkOwlItems = hawkOwlItems.slice(0, 2); // Keep only Employee Insights and My Timesheet
      delete hawkOwlItems[1].children;
      // Add the Apps tab back for staff
      hawkOwlItems.push({
        title: 'Apps',
        path: '/dashboard/hawkowl?tab=apps',
        icon: <Computer />
      });
    }

    sidebarConfig = [
      {
        subheader: 'HOME',
        items: hawkOwlItems
      }
    ];
  }

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  const handleLogoutClick = () => {
    setOpenLogoutDialog(true);
  };

  const handleLogoutConfirm = async () => {
    try {
      await logout();
      navigate('/');
      if (isMountedRef.current) {
        onCloseSidebar();
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to logout', { variant: 'error' });
    } finally {
      setOpenLogoutDialog(false);
    }
  };

  const handleLogoutCancel = () => {
    setOpenLogoutDialog(false);
  };

  const isHawkOwl = pathname.startsWith('/dashboard/hawkowl');

  const renderContent = (
    <Scrollbar
      sx={{
        height: '100%',
        '& .simplebar-content': {
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >


      <Box sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1100,
        bgcolor: 'background.default'
      }}>
        {/* Minimize/Expand Button / Handdy Logo */}
        {!isHawkOwl ? (
          <Box sx={{ px: 2.5, py: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
            <Tooltip title={isCollapsed ? "Expand sidebar" : "Minimize sidebar"}>
              <IconButton
                onClick={onToggleCollapse}
                size="small"
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                {isCollapsed ? <ChevronRight /> : <ChevronLeft />}
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          <Box sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {!isCollapsed && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <HawkOwlLogo size={32} showGlow />
                <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 800, letterSpacing: '-0.5px', mb: 0 }}>
                  HawkOwl
                </Typography>
              </Box>
            )}
            <Tooltip title={isCollapsed ? "Expand sidebar" : "Minimize sidebar"}>
              <IconButton
                onClick={onToggleCollapse}
                size="small"
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                {isCollapsed ? <ChevronRight /> : <ChevronLeft />}
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {/* Back to Dashboard Button (Only for HawkOwl) */}
        {isHawkOwl && (
          <Box sx={{ 
            px: 2.5, 
            pb: 2, 
            display: 'flex', 
            justifyContent: 'center'
          }}>
            {isCollapsed ? (
              <IconButton
                component={RouterLink}
                to="/dashboard/app"
                sx={{
                  width: 40,
                  height: 40,
                  color: 'common.white',
                  bgcolor: 'primary.main',
                  '&:hover': {
                    bgcolor: 'primary.dark'
                  }
                }}
              >
                <ArrowBack sx={{ fontSize: '20px' }} />
              </IconButton>
            ) : (
              <Button
                component={RouterLink}
                to="/dashboard/app"
                fullWidth
                variant="contained"
                color="primary"
                startIcon={<ArrowBack />}
                sx={{
                  boxShadow: 'none',
                  '&:hover': {
                    boxShadow: 'none'
                  }
                }}
              >
                Back To Dashboard
              </Button>
            )}
          </Box>
        )}
      </Box>



      {/* Avatar box - only shown if not HawkOwl */}
      {!isHawkOwl && (
        <>
          <Box sx={{ mb: 1, mx: isCollapsed ? 1 : 2.5 }}>
            <Link
            underline="none"
            component={RouterLink}
            to={PATH_DASHBOARD.user.account}
          >
            <AccountStyle sx={{
              flexDirection: isCollapsed ? 'column' : 'row',
              alignItems: 'center',
              justifyContent: isCollapsed ? 'center' : 'flex-start',
              textAlign: isCollapsed ? 'center' : 'left',
              px: isCollapsed ? 0.5 : 2.5,
              py: isCollapsed ? 1 : 2,
              overflow: 'hidden'
            }}>
              <MyAvatar sx={{ 
                width: isCollapsed ? 36 : 52, 
                height: isCollapsed ? 36 : 52,
                minWidth: isCollapsed ? 36 : 52,
                border: (theme) => `solid 2px ${theme.palette.background.paper}`,
                boxShadow: (theme) => `0 4px 10px 0 ${theme.palette.mode === 'light' ? 'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.3)'}`,
                bgcolor: (theme) => theme.palette.mode === 'light' ? '#fff' : theme.palette.grey[700],
                color: (theme) => theme.palette.mode === 'light' ? 'text.primary' : '#fff',
                '& .MuiAvatar-img': {
                  objectFit: 'cover'
                }
              }} />
              {!isCollapsed && (
                <Box sx={{ ml: 2, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <Typography variant="subtitle2" noWrap sx={{ color: 'text.primary', fontWeight: 600 }}>
                    {user.person_name}
                  </Typography>
                  
                  {user.staff_role && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                      <Typography variant="body2" noWrap sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.8rem' }}>
                        {user.staff_role}
                      </Typography>
                    </Box>
                  )}
                  
                  <Typography variant="caption" noWrap sx={{ color: 'text.disabled', mt: user.staff_role ? 0.25 : 0.5 }}>
                    {user.user_role}
                  </Typography>
                </Box>
              )}
            </AccountStyle>
          </Link>
        </Box>

        {/* Dark Mode Toggle */}
        <Box sx={{ mb: 1 }}>
          <DarkModeToggle isCollapsed={isCollapsed} />
        </Box>
        </>
      )}

      <NavSection navConfig={sidebarConfig} isCollapsed={isCollapsed} />

      <Box sx={{ flexGrow: 1 }} />

      {/* Logout Button */}
      <Box sx={{ 
        px: 2.5, 
        pt: 2,
        pb: 2, 
        display: 'flex', 
        justifyContent: 'center',
        position: 'sticky',
        bottom: 0,
        zIndex: 1100,
        bgcolor: 'background.default'
      }}>
        {isCollapsed ? (
          <IconButton
            onClick={handleLogoutClick}
            sx={{
              width: 40,
              height: 40,
              color: 'text.primary',
              border: '1px solid',
              borderColor: (theme) => theme.palette.mode === 'light' ? 'grey.300' : 'grey.700',
              borderRadius: '50%',
              '&:hover': {
                borderColor: (theme) => theme.palette.mode === 'light' ? 'grey.400' : 'grey.600',
                backgroundColor: 'action.hover'
              }
            }}
          >
            <Logout sx={{ fontSize: '20px' }} />
          </IconButton>
        ) : (
          <Button
            fullWidth
            color="inherit"
            variant="outlined"
            onClick={handleLogoutClick}
            startIcon={<Logout />}
            sx={{
              borderColor: (theme) => theme.palette.mode === 'light' ? 'grey.300' : 'grey.700',
              color: 'text.primary',
              '&:hover': {
                borderColor: (theme) => theme.palette.mode === 'light' ? 'grey.400' : 'grey.600',
                backgroundColor: 'action.hover'
              }
            }}
          >
            Logout
          </Button>
        )}
      </Box>

      {/* <Box sx={{ px: 2.5, pb: 3, mt: 2 }}>
        <DocStyle>
          <Box component="img" src={`/static/auth/customer_service.png`} sx={{ width: 36, height: 36 }} />
          <Typography gutterBottom variant="subtitle1" sx={{ color: 'grey.800' }}>
            skyniche Technologies - Client Support Portal
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'grey.600' }}>
            Delivering reliable support, every step of the way.
            <br /> Submit tickets, track updates, and connect with our experts.
          </Typography>
        </DocStyle>
      </Box> */}

      {/* <Box sx={{ px: 2.5, pb: 3, mt: 10 }}>
        <DocStyle>
          <DocIcon sx={{ width: 36, height: 36, mb: 2 }} />
          <Typography gutterBottom variant="subtitle1" sx={{ color: 'grey.800' }}>
            Hi, {user.person_name}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'grey.600' }}>
            Need help?
            <br /> Please check our docs
          </Typography>
          <Button fullWidth to={PATH_DOCS.root} variant="contained" component={RouterLink}>
            Documentation
          </Button>
        </DocStyle>
      </Box> */}
    </Scrollbar>
  )

  return (
    <RootStyle>
      <MHidden width="lgUp">
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{
            sx: {
              width: DRAWER_WIDTH,
              bgcolor: 'background.default'
            },
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>

      <MHidden width="lgDown">
        <Box
          sx={{
            width: isCollapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
            minWidth: isCollapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
            maxWidth: isCollapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
            height: '100vh',
            bgcolor: 'background.default',
            transition: 'width 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
            borderRight: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
            position: 'relative'
          }}
        >
          {renderContent}
          {!isCollapsed && (
            <Stack alignItems={'center'} justifyContent={'center'}>
              <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', fontSize: '14px', pb: 4, fontWeight: 'bold' }}>
                V {VERSION}
              </Typography>
            </Stack>
          )}
        </Box>
      </MHidden>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={openLogoutDialog}
        onClose={handleLogoutCancel}
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="logout-dialog-title">
          Confirm Logout
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to logout?
            {/* You will need to sign in again to access your account. */}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleLogoutCancel}
            color="inherit"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleLogoutConfirm}
            color="error"
            variant="contained"
            autoFocus
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </RootStyle>
  )
}
